import type { FC } from 'react';
import React, { useEffect } from 'react';
import cn from 'classnames';

import { defaultHandler } from './config';
import type { TAutoComplete, IScrollableConfig } from './models';
import defaultStyles from './styles/styles.css';
import { getScroll } from './helpers/actions';

export const Autocomplete: FC<TAutoComplete> = ({
    autocompleteRef,
    highlightedItem = -1,
    onSelect = defaultHandler,
    dataTestId = 'default',
    options,
    position,
    styles: injectedStyles,
}) => {
    const styles = { ...defaultStyles, ...injectedStyles };

    useEffect(() => {
        if (highlightedItem < 0) return;

        if (autocompleteRef.current) {
            const top = getScroll(highlightedItem, autocompleteRef.current.scrollTop);

            autocompleteRef.current.scrollTo?.({ top });
        }
    }, [highlightedItem, autocompleteRef]);

    // Пропс что бы отображался скролл на ios, при использовании внутри site-mobile-bottom-sheet
    const scrollableConfig: IScrollableConfig = {
        'data-allow-touch-move': true,
    };

    return (
        <ul
            ref={autocompleteRef}
            /** Класс `ym-hide-content` нужен, чтобы скрывать конфиденциальные данные в вебвизоре
             * https://jira.moscow.alfaintra.net/browse/SITEDK-952
             */
            className={cn(styles.autocomplete, styles[position], {
                'ym-hide-content': !process.env.DISABLE_HIDE_DATA_FOR_WEBVISOR,
            })}
            data-test-id={`${dataTestId}-autocomplete`}
            role="listbox"
            tabIndex={-1}
            {...scrollableConfig}
        >
            {options.map((option, index) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                    key={option.id}
                    aria-selected={index === highlightedItem}
                    className={styles.option}
                    data-test-id={`${dataTestId}-autocomplete-item[${index}]`}
                    onClick={event => onSelect({ event, value: index })}
                    role="option"
                >
                    {option.label}
                </li>
            ))}
        </ul>
    );
};
